import { portalFetch } from '../lib/apiClient';

/**
 * Password flows for the portal storefront. All four endpoints proxy
 * through crm-api → login. The first three are anonymous (the user is
 * recovering or setting up access); change-password requires an active
 * portal session.
 */

export interface ValidateResetTokenResponse {
	valid: boolean;
	/** Masked email (or phone) to show on the page. */
	email?: string;
	/** Full sign-in name, only for the form's hidden password-manager field. */
	username?: string;
}

/**
 * Trigger a password-reset email/SMS for the given username (email or
 * phone). Always resolves to `{ ok: 1 }` to avoid leaking user existence.
 */
export async function forgotPassword(username: string): Promise<{ ok: 1 }> {
	return portalFetch<{ ok: 1 }>('/auth/forgot-password', '', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ username }),
	});
}

/**
 * Validate a reset/welcome token before showing the password form.
 * Returns { valid: false } when expired/invalid; the caller redirects
 * the user back to forgot-password in that case.
 */
export async function validateResetToken(
	token: string,
): Promise<ValidateResetTokenResponse> {
	return portalFetch<ValidateResetTokenResponse>(
		'/auth/validate-reset-token',
		'',
		{
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ token }),
		},
	);
}

/**
 * Set the user's password using a one-time token (welcome email or
 * forgot-password reset). Throws on invalid/expired tokens or rejected
 * passwords; the form surfaces the error.
 */
export async function resetPassword(
	token: string,
	password: string,
): Promise<{ message: string }> {
	return portalFetch<{ message: string }>('/auth/reset-password', '', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ token, password }),
	});
}

/**
 * Change the password for the currently-authenticated portal user.
 * Requires the current password — login verifies it server-side.
 */
export async function changePassword(
	token: string,
	currentPassword: string,
	newPassword: string,
): Promise<{ message: string }> {
	return portalFetch<{ message: string }>('/auth/change-password', token, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ currentPassword, newPassword }),
	});
}
